import {BasicReducerState} from "../BasicReducerState";
import {ProjectTeam} from "../../models/teams/Team";
import {
    CREATE_TEAM,
    CREATE_TEAM_ERROR,
    CREATE_TEAM_SUCCESS,
    FETCH_TEAM_BY_ID,
    FETCH_TEAM_BY_ID_ERROR,
    FETCH_TEAM_BY_ID_SUCCESS,
    FETCH_TEAMS,
    FETCH_TEAMS_ERROR,
    FETCH_TEAMS_SUCCESS,
    TeamActionTypes, UPDATE_TEAM, UPDATE_TEAM_ERROR, UPDATE_TEAM_SUCCESS
} from "../../actions/projects/TeamActions";
import {
    errorFetchingRequestState,
    getInitialRequestState,
    startFetchingRequestState,
    successFetchingRequestState
} from "../../models/common/RequestState";
import {Nullable} from "../../models/common/Nullable";

/**
 * Represents the state of a team within the application, extending a basic reducer state.
 *
 * This interface is designed to manage and maintain the structure of team-related data,
 * allowing for proper handling, manipulation, and retrieval of the state of teams and their members.
 *
 * @template T - Defines a generic type for the individual team member data structure.
 *
 * Extends from:
 * - `BasicReducerState` to inherit properties and methods that enable common reducer state
 *   functionality, such as maintaining a list of teams and handling individual team actions.
 *
 * The type parameters include:
 * - A nullable array of teams, where each team is represented as an array of `TeamMember` objects.
 * - A single team entity containing one or more `TeamMember` objects.
 */
export interface TeamState extends BasicReducerState< Nullable< ProjectTeam[] >, Nullable< ProjectTeam > > {}

/**
 * Represents the initial state of a team's data.
 *
 * @typedef {Object} TeamState
 * @property {number} totalEntries - The total number of entries for the team.
 * @property {Object} entries - The state of the team's entries.
 * @property {Object|null} entries.current - The current entry, or null if none exists.
 * @property {Object} entries.request - The request state for fetching entries.
 * @property {Object} detailedEntries - A mapping of detailed entry information, identified by ID.
 * @property {Object} changingRequest - The request state for modifying entries.
 *
 * @type {TeamState}
 */
const initialTeamState: TeamState = {
    totalEntries: 0,
    entries: {
        current: null,
        request: getInitialRequestState()
    },
    detailedEntries: {},
    changingRequest: getInitialRequestState()
}

/**
 * Reducer function that manages the state of teams based on dispatched actions.
 *
 * @param {TeamState} [state=initialTeamState] - The current state of the teams. Defaults to the initial state if not provided.
 * @param {TeamActionTypes} action - The action object that describes the type of state update to be performed.
 * @return {TeamState} The updated state of the teams after processing the provided action.
 */
export function TeamReducer(
    state: TeamState = initialTeamState,
    action: TeamActionTypes
) {

    switch (action.type) {
        case FETCH_TEAMS:
            return {
                ...state,
                entries: {
                    ...state.entries,
                    request: startFetchingRequestState()
                }
            };
        case FETCH_TEAMS_SUCCESS:
            return {
                ...state,
                totalEntries: action.teams.totalEntries,
                entries: {
                    current: action.teams.entries,
                    request: successFetchingRequestState()
                }
            };
        case FETCH_TEAMS_ERROR:
            return {
                ...state,
                entries: {
                    ...state.entries,
                    request: errorFetchingRequestState(action.error)
                }
            };
        case FETCH_TEAM_BY_ID:
            return {
                ...state,
                detailedEntries: {
                    ...state.detailedEntries,
                    [action.id.rawValue]: {
                        entry: null,
                        request: startFetchingRequestState()
                    }
                }
            };
        case FETCH_TEAM_BY_ID_SUCCESS:
            return {
                ...state,
                detailedEntries: {
                    ...state.detailedEntries,
                    [action.id.rawValue]: {
                        entry: action.payload,
                        request: successFetchingRequestState()
                    }
                }
            };
        case FETCH_TEAM_BY_ID_ERROR:
            return {
                ...state,
                detailedEntries: {
                    ...state.detailedEntries,
                    [action.id.rawValue]: {
                        ...state.detailedEntries[action.id.rawValue],
                        request: errorFetchingRequestState(action.error)
                    }
                }
            };
        case CREATE_TEAM:
            return {
                ...state,
                changingRequest: startFetchingRequestState()
            };
        case CREATE_TEAM_SUCCESS:
            return {
                ...state,
                changingRequest: successFetchingRequestState()
            };
        case CREATE_TEAM_ERROR:
            return {
                ...state,
                changingRequest: errorFetchingRequestState(action.error)
            };

        case UPDATE_TEAM:
            return {
                ...state,
                changingRequest: startFetchingRequestState()
            };
        case UPDATE_TEAM_SUCCESS:
            return {
                ...state,
                changingRequest: successFetchingRequestState()
            };
        case UPDATE_TEAM_ERROR:
            return {
                ...state,
                changingRequest: errorFetchingRequestState(action.error)
            };
        default:
            return state;
    }

}